const CORE = [
  {
    name: "Grocery & Supermarkets",
    note:
      "High-volume cart fleets, high customer exposure, high standards. This is where we built our expertise.",
  },
  {
    name: "Independent Grocers",
    note:
      "Local ownership, local service. We work directly with decision-makers — no national procurement delays.",
  },
  {
    name: "Pharmacy & Drug Stores",
    note:
      "Cart fleets serving health-conscious customers where sanitation standards carry real weight.",
  },
  {
    name: "Dollar & Discount Retail",
    note:
      "High traffic, high turnover, carts that take a beating. Scheduled maintenance prevents early replacement.",
  },
];

const EXTENDED = [
  { name: "Home Improvement", note: "Large flatbeds, heavy-use carts, outdoor exposure. We handle volume and conditions." },
  { name: "Healthcare & Medical", note: "Hospitals and campuses with wheelchair fleets and exterior assets." },
  { name: "Hospitality & Hotels", note: "Luggage carts, valet areas, and exterior assets. First impressions start before the lobby." },
  { name: "Airports & Transit", note: "Baggage carts, wheelchair fleets, and high-volume public-touch surfaces." },
];

export default function WhoWeServe() {
  return (
    <section
      id="who"
      data-testid="who-section"
      className="py-16 md:py-32 bg-[var(--xw-ink)] text-white border-b border-[var(--xw-line)] xw-noise"
    >
      <div className="xw-container relative z-10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono-xw text-[11px] tracking-[0.22em] uppercase text-[var(--xw-orange)]">
                § 03 — Who We Serve
              </span>
              <span className="h-px w-16 bg-white/30" />
            </div>
            <h2
              data-testid="who-headline"
              className="font-display font-black uppercase tracking-[-0.03em] leading-[0.9]"
              style={{ fontSize: "clamp(2rem, 7vw, 5rem)" }}
            >
              Any Property. <br />
              <span className="italic font-medium" style={{ color: "var(--xw-orange)" }}>
                Any Exterior.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[16px] md:text-[17px] leading-relaxed text-white/70">
              Our model was built around grocery and retail environments. But
              our exterior operations framework applies to any commercial
              property with assets outside its four walls.
            </p>
          </div>
        </div>

        {/* Core specialty */}
        <div className="mt-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono-xw text-[10px] tracking-[0.22em] uppercase text-white/50">
              Core Specialty
            </span>
            <span className="flex-1 h-px bg-white/15" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/15">
            {CORE.map((c, i) => (
              <div
                key={c.name}
                data-testid={`who-core-${i}`}
                className="p-6 sm:p-7 md:p-8 border-r border-b border-white/15 group hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center justify-between mb-5 md:mb-6">
                  <span className="font-mono-xw text-[10px] tracking-[0.22em] uppercase text-[var(--xw-orange)]">
                    0{i + 1}
                  </span>
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "var(--xw-orange)" }}
                  />
                </div>
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">
                  {c.name}
                </h3>
                <p className="mt-3 text-white/60 text-[13.5px] leading-snug">
                  {c.note}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Extended */}
        <div className="mt-14">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono-xw text-[10px] tracking-[0.22em] uppercase text-white/50">
              Also Serving
            </span>
            <span className="flex-1 h-px bg-white/15" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {EXTENDED.map((e, i) => (
              <div
                key={e.name}
                data-testid={`who-ext-${i}`}
                className="py-4 border-t border-white/20"
              >
                <h4 className="font-display font-semibold text-lg tracking-tight">
                  {e.name}
                </h4>
                <p className="mt-2 text-white/55 text-[13px] leading-snug">
                  {e.note}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* marquee */}
      <div className="mt-12 md:mt-20 border-t border-b border-white/15 overflow-hidden py-4 md:py-6">
        <div className="xw-marquee-track whitespace-nowrap font-display font-black uppercase text-2xl sm:text-4xl md:text-5xl tracking-tight">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-6 sm:gap-12">
              <span>Grocery</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Pharmacy</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Home Improvement</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Healthcare</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Hospitality</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Airports</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
              <span>Discount Retail</span>
              <span style={{ color: "var(--xw-orange)" }}>✱</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
